import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Html5Qrcode } from "html5-qrcode";
import { QrCode, Camera, CameraSlash, CheckCircle, WarningCircle, FlagCheckered, Keyboard } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, useApi, todayISO } from "../lib/api";
import { GlassCard, Loader, Empty } from "./kit";

const beep = (ok) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    o.frequency.value = ok ? 880 : 220;
    o.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.12);
    navigator.vibrate?.(ok ? 60 : [80, 60, 80]);
  } catch (e) { /* audio not available */ }
};

function useScanner(onCode) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  const cb = useRef(onCode);
  cb.current = onCode;
  const stop = useCallback(async () => {
    if (ref.current) {
      await ref.current.stop().catch(() => {});
      ref.current.clear();
      ref.current = null;
    }
    setOn(false);
  }, []);
  const start = async () => {
    try {
      ref.current = new Html5Qrcode("qr-reader");
      await ref.current.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 220, height: 220 } }, (txt) => cb.current(txt), () => {});
      setOn(true);
    } catch (e) {
      ref.current = null;
      toast.error("Camera not available — allow camera access or type the admission number.");
    }
  };
  useEffect(() => () => { stop(); }, [stop]);
  return { on, start, stop };
}

function LastScan({ last }) {
  return (
    <AnimatePresence mode="wait">
      {last && (
        <motion.div key={last.at} initial={{ opacity: 0, y: 8, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }}
          className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${last.ok ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200" : "border-rose-400/30 bg-rose-500/10 text-rose-200"}`} data-testid="qr-last-scan">
          {last.ok ? <CheckCircle size={22} weight="fill" /> : <WarningCircle size={22} weight="fill" />}
          <span className="text-sm font-semibold">{last.text}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Roster({ rows, present, onTap }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="qr-roster">
      {rows.map((r) => {
        const p = present.has(r.id);
        return (
          <button key={r.id} onClick={() => onTap(r)} data-testid={`qr-student-${r.admission_no}`}
            className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-2.5 text-left transition-colors duration-200 ${p ? "border-emerald-400/30 bg-emerald-500/10" : "border-white/5 bg-white/[0.03] hover:bg-white/[0.06]"}`}>
            <span className="min-w-0"><span className="block truncate text-sm font-semibold text-white">{r.name}</span><span className="font-mono text-[11px] text-zinc-500">{r.admission_no} · Roll {r.roll_no ?? "—"}</span></span>
            <span className={`text-xs font-bold ${p ? "text-emerald-300" : "text-zinc-500"}`}>{p ? "Present" : "Not scanned"}</span>
          </button>
        );
      })}
    </div>
  );
}

export function QrAttendance({ classes }) {
  const [cls, setCls] = useState(classes[0]?.id || "");
  const { data: mine } = useApi("/id-cards");
  const own = mine?.classes.length === 1 ? mine.classes[0].id : null;
  useEffect(() => {
    if (own) setCls(own);
  }, [own]);
  const date = todayISO();
  const key = `qr-att:${cls}:${date}`;
  const { data, loading } = useApi(cls ? `/attendance?class_id=${cls}&date=${date}` : null);
  const [present, setPresent] = useState(new Set());
  const [last, setLast] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const recent = useRef({});
  const rows = data?.students || [];
  useEffect(() => {
    setPresent(new Set(JSON.parse(localStorage.getItem(key) || "[]")));
    setLast(null);
  }, [key]);
  const save = (next) => {
    setPresent(next);
    localStorage.setItem(key, JSON.stringify([...next]));
  };
  const handle = (raw) => {
    const code = String(raw).trim().toUpperCase();
    const now = Date.now();
    if (!code || now - (recent.current[code] || 0) < 2500) return;
    recent.current[code] = now;
    const s = rows.find((r) => String(r.admission_no).toUpperCase() === code);
    if (!s) {
      beep(false);
      setLast({ ok: false, text: `${code} is not in this class`, at: now });
      return;
    }
    beep(true);
    setLast({ ok: true, text: present.has(s.id) ? `${s.name} already marked present` : `${s.name} marked present`, at: now });
    if (!present.has(s.id)) save(new Set([...present, s.id]));
  };
  const scanner = useScanner(handle);
  const toggle = (r) => {
    const next = new Set(present);
    next.has(r.id) ? next.delete(r.id) : next.add(r.id);
    save(next);
  };
  const finish = async () => {
    const absent = rows.length - present.size;
    if (!window.confirm(`${present.size} present, ${absent} will be marked absent and their parents alerted. Save attendance?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/attendance", { class_id: cls, date, records: rows.map((x) => ({ student_id: x.id, status: present.has(x.id) ? "P" : "A" })) });
      toast.success(`Saved · ${r.data.present}/${r.data.total} present`);
      localStorage.removeItem(key);
      await scanner.stop();
      setPresent(new Set());
      setLast(null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <GlassCard className="p-4 sm:p-6" data-testid="qr-attendance">
      <div className="mb-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <select className="field sm:!w-56" value={cls} onChange={(e) => { scanner.stop(); setCls(e.target.value); }} data-testid="qr-class-select">
          {classes.map((c) => <option key={c.id} value={c.id}>{c.name} {c.section}</option>)}
        </select>
        <span className="text-sm text-zinc-400">Today · <span className="text-white" data-testid="qr-count">{present.size}/{rows.length}</span> present</span>
        <button className={`sm:ml-auto ${scanner.on ? "btn-ghost" : "btn-primary"}`} onClick={scanner.on ? scanner.stop : scanner.start} data-testid="qr-camera-btn">
          {scanner.on ? <><CameraSlash size={16} /> Stop camera</> : <><Camera size={16} /> Start scanning</>}
        </button>
      </div>
      {data?.marked && <p className="mb-4 text-xs text-zinc-400" data-testid="qr-marked-note">Attendance already saved today by {data.marked_by} — finishing will update it.</p>}
      <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
        <div className="space-y-3">
          <div className="relative aspect-square overflow-hidden rounded-3xl border border-white/10 bg-black/60">
            <div id="qr-reader" className="h-full w-full [&_video]:!h-full [&_video]:!w-full [&_video]:object-cover" data-testid="qr-reader" />
            {!scanner.on && <div className="absolute inset-0 grid place-items-center text-center text-sm text-zinc-500"><div><QrCode size={46} weight="duotone" className="mx-auto mb-2 text-blue-300" />Point the camera at the QR on a student's ID card</div></div>}
          </div>
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); handle(typed); recent.current = {}; setTyped(""); }}>
            <div className="relative flex-1"><Keyboard size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input className="field !pl-9" placeholder="Or type admission no" value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="qr-manual-input" /></div>
            <button className="btn-ghost" data-testid="qr-manual-btn">Mark</button>
          </form>
          <LastScan last={last} />
        </div>
        <div>
          {loading ? <Loader /> : !rows.length ? <Empty hint="No students in this class." /> : <Roster rows={rows} present={present} onTap={toggle} />}
          <div className="mt-5 flex justify-end">
            <button className="btn-primary" disabled={busy || !rows.length} onClick={finish} data-testid="qr-finish-btn"><FlagCheckered size={16} /> Finish · mark rest absent</button>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}
